import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    ArrowLeft, Calendar, X, Loader2, TrendingUp, Trophy, AlertTriangle,
    Map, Tag, Compass, FileCheck, Percent,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_TOTAL = '#94A3B8';
const COLOR_ACCEPTE = '#10B981';
const COLOR_TAUX = '#C9A962';

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};

function FilterBar({ datePreset, dateFrom, dateTo, onPreset, onFrom, onTo, onClear }) {
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                {[
                    { v: 'all', label: 'Tout' },
                    { v: 'today', label: "Aujourd'hui" },
                    { v: '7d', label: '7 jours' },
                    { v: '30d', label: '30 jours' },
                    { v: 'custom', label: 'Personnalisé' },
                ].map(p => (
                    <button key={p.v} onClick={() => onPreset(p.v)}
                        className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${datePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                        data-testid={`devis-preset-${p.v}`}
                    >{p.label}</button>
                ))}
            </div>
            {datePreset === 'custom' && (
                <div className="flex items-center gap-1.5">
                    <input type="date" value={dateFrom} onChange={(e) => onFrom(e.target.value)} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="devis-date-from" />
                    <span className="text-gray-400 text-[10px]">→</span>
                    <input type="date" value={dateTo} onChange={(e) => onTo(e.target.value)} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="devis-date-to" />
                </div>
            )}
            {(dateFrom || dateTo) && (
                <button onClick={onClear} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                    <X className="w-3 h-3" /> Effacer
                </button>
            )}
        </div>
    );
}

function ComparisonChart({ rows, mode }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="h-[280px] flex flex-col items-center justify-center text-gray-400 text-sm">
                <FileCheck className="w-9 h-9 mb-2 text-gray-300" />
                Aucun devis sur cette période
            </div>
        );
    }
    if (mode === 'taux') {
        return (
            <ResponsiveContainer width="100%" height={280}>
                <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} interval={0} angle={-20} textAnchor="end" />
                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} domain={[0, 100]} unit="%" />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} formatter={(v) => `${v} %`} />
                    <Bar dataKey="taux" name="Taux d'acceptation" radius={[6, 6, 0, 0]}>
                        {rows.map((r, i) => (
                            <Cell key={i} fill={r.taux >= 50 ? COLOR_ACCEPTE : r.taux >= 25 ? COLOR_TAUX : '#EF4444'} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    }
    return (
        <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} interval={0} angle={-20} textAnchor="end" />
                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }} />
                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="total" name="Total devis" fill={COLOR_TOTAL} radius={[6, 6, 0, 0]} />
                <Bar dataKey="accepte" name="Acceptés" fill={COLOR_ACCEPTE} radius={[6, 6, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}

function Section({ title, icon: Icon, accent, rows, testId }) {
    const [mode, setMode] = useState('volume'); // volume | taux
    return (
        <Card className="border-t-4 shadow-sm" style={{ borderTopColor: accent }} data-testid={testId}>
            <CardContent className="p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
                            <Icon className="h-4 w-4" style={{ color: accent }} />
                        </div>
                        <h3 className="font-bold text-[#1B365D] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                    </div>
                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {[
                            { v: 'volume', label: 'Volumes' },
                            { v: 'taux', label: 'Taux %' },
                        ].map(m => (
                            <button key={m.v} onClick={() => setMode(m.v)}
                                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${mode === m.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`${testId}-mode-${m.v}`}
                            >{m.label}</button>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-50/50 border border-gray-100 rounded-lg p-3">
                    <ComparisonChart rows={rows} mode={mode} />
                </div>
                {/* Mini-table */}
                {rows && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-gray-500 uppercase">
                                <tr><th className="text-left py-2">Élément</th><th className="text-right">Total</th><th className="text-right">Acceptés</th><th className="text-right">Taux</th></tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.key} className="border-t border-gray-100">
                                        <td className="py-1.5 text-[#1B365D]">{r.label}</td>
                                        <td className="text-right text-gray-700">{r.total}</td>
                                        <td className="text-right text-emerald-700 font-semibold">{r.accepte}</td>
                                        <td className="text-right" style={{ color: r.taux >= 50 ? '#059669' : r.taux >= 25 ? '#C9A962' : '#DC2626' }}>{r.taux} %</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function StatistiquesDevisAcceptation() {
    useStatsBlockGuard('stats_devis');
    const navigate = useNavigate();
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const applyDatePreset = (p) => {
        setDatePreset(p);
        const today = new Date(); const iso = toISO(today);
        if (p === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (p === 'today') { setDateFrom(iso); setDateTo(iso); return; }
        if (p === '7d') { const d = new Date(); d.setDate(d.getDate() - 6); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === '30d') { const d = new Date(); d.setDate(d.getDate() - 29); setDateFrom(toISO(d)); setDateTo(iso); return; }
    };
    const clearDates = () => { setDatePreset('all'); setDateFrom(''); setDateTo(''); };

    useEffect(() => {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/devis-acceptation`, { params, withCredentials: true })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const totals = data?.totals || { total_devis: 0, total_accepte: 0, taux_global: 0 };
    const top_sources = data?.top_sources || [];
    const top_dept = data?.top_departements || [];
    const alertes = data?.alertes_sources || [];
    const threshold = data?.weak_threshold ?? 25;

    const tauxColor = useMemo(() => {
        if (totals.taux_global >= 50) return '#10B981';
        if (totals.taux_global >= 25) return '#C9A962';
        return '#EF4444';
    }, [totals.taux_global]);

    return (
        <Layout>
            <div className="space-y-5 animate-fadeIn" data-testid="stats-devis-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 w-8 p-0" data-testid="stats-devis-back-btn" aria-label="Retour">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="stats-devis-title">
                                Devis — Taux d'acceptation
                            </h1>
                            <p className="text-gray-500 mt-0.5 text-sm">Analyse des acceptations par source, statut et département</p>
                        </div>
                    </div>
                </div>

                {/* Filter */}
                <Card className="shadow-sm">
                    <CardContent className="p-3 sm:p-4">
                        <FilterBar
                            datePreset={datePreset}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onPreset={applyDatePreset}
                            onFrom={(v) => { setDateFrom(v); setDatePreset('custom'); }}
                            onTo={(v) => { setDateTo(v); setDatePreset('custom'); }}
                            onClear={clearDates}
                        />
                    </CardContent>
                </Card>

                {/* Totals KPI */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="devis-totals">
                    <Card className="border-t-4 border-t-[#1B365D] shadow-sm">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="h-11 w-11 rounded-full bg-[#1B365D]/10 flex items-center justify-center"><FileCheck className="w-5 h-5 text-[#1B365D]" /></div>
                            <div>
                                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Total devis</div>
                                <div className="text-3xl font-bold text-[#1B365D]" data-testid="devis-total">{totals.total_devis}</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-t-4 border-t-emerald-500 shadow-sm">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="h-11 w-11 rounded-full bg-emerald-50 flex items-center justify-center"><FileCheck className="w-5 h-5 text-emerald-600" /></div>
                            <div>
                                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Devis acceptés</div>
                                <div className="text-3xl font-bold text-emerald-700" data-testid="devis-accepted">{totals.total_accepte}</div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-t-4 shadow-sm" style={{ borderTopColor: tauxColor }}>
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ backgroundColor: `${tauxColor}1A` }}><Percent className="w-5 h-5" style={{ color: tauxColor }} /></div>
                            <div>
                                <div className="text-[11px] text-gray-500 uppercase tracking-wide">Taux d'acceptation global</div>
                                <div className="text-3xl font-bold" style={{ color: tauxColor }} data-testid="devis-taux-global">{totals.taux_global} %</div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                )}

                {!loading && data && (
                    <>
                        {/* Sections par source / statut / département */}
                        <Section
                            title="Par source"
                            icon={Tag}
                            accent="#1B365D"
                            rows={data.by_source}
                            testId="section-source"
                        />
                        <Section
                            title="Par statut"
                            icon={Compass}
                            accent="#8B5CF6"
                            rows={data.by_statut}
                            testId="section-statut"
                        />
                        <Section
                            title="Par département"
                            icon={Map}
                            accent="#0EA5E9"
                            rows={data.by_departement}
                            testId="section-dept"
                        />

                        {/* Tops + alertes */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <Card className="border-t-4 border-t-emerald-500 shadow-sm" data-testid="top-sources">
                                <CardContent className="p-5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-9 w-9 rounded-full bg-emerald-50 flex items-center justify-center"><Trophy className="w-4 h-4 text-emerald-600" /></div>
                                        <h3 className="font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Top 3 sources qui convertissent</h3>
                                    </div>
                                    {top_sources.length === 0 ? (
                                        <p className="text-sm text-gray-400">Aucune donnée</p>
                                    ) : (
                                        <ol className="space-y-2">
                                            {top_sources.map((s, i) => (
                                                <li key={s.key} className="flex items-center justify-between p-2 bg-emerald-50/40 rounded-lg">
                                                    <span className="flex items-center gap-2 text-sm">
                                                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-500 text-white text-xs font-bold">{i + 1}</span>
                                                        <span className="font-semibold text-[#1B365D]">{s.label}</span>
                                                    </span>
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-500">{s.accepte}/{s.total}</span>
                                                        <span className="text-sm font-bold text-emerald-700">{s.taux} %</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border-t-4 border-t-sky-500 shadow-sm" data-testid="top-departements">
                                <CardContent className="p-5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-9 w-9 rounded-full bg-sky-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-sky-600" /></div>
                                        <h3 className="font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Top 3 départements rentables</h3>
                                    </div>
                                    {top_dept.length === 0 ? (
                                        <p className="text-sm text-gray-400">Aucune donnée</p>
                                    ) : (
                                        <ol className="space-y-2">
                                            {top_dept.map((d, i) => (
                                                <li key={d.key} className="flex items-center justify-between p-2 bg-sky-50/40 rounded-lg">
                                                    <span className="flex items-center gap-2 text-sm">
                                                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-sky-500 text-white text-xs font-bold">{i + 1}</span>
                                                        <span className="font-semibold text-[#1B365D]">Dépt. {d.label}</span>
                                                    </span>
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-500">{d.accepte}/{d.total}</span>
                                                        <span className="text-sm font-bold text-sky-700">{d.taux} %</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="border-t-4 border-t-red-500 shadow-sm" data-testid="alertes-sources">
                                <CardContent className="p-5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-9 w-9 rounded-full bg-red-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-600" /></div>
                                        <h3 className="font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Alertes sources faibles</h3>
                                    </div>
                                    <p className="text-[11px] text-gray-500">Sources avec un taux &lt; {threshold} %</p>
                                    {alertes.length === 0 ? (
                                        <p className="text-sm text-emerald-600 font-medium">Aucune alerte — toutes les sources performent bien</p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {alertes.map((s) => (
                                                <li key={s.key} className="flex items-center justify-between p-2 bg-red-50/40 rounded-lg">
                                                    <span className="flex items-center gap-2 text-sm">
                                                        <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                                                        <span className="font-semibold text-[#1B365D]">{s.label}</span>
                                                    </span>
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-500">{s.accepte}/{s.total}</span>
                                                        <span className="text-sm font-bold text-red-600">{s.taux} %</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
